import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { v4 } from 'uuid';
import { USER_PREFERENCE_SETS } from 'common/utils/constants';
import {
  useSearchPreferencesQuery,
  useCreatePreferenceMutation,
  useUpdatePreferenceMutation,
  useDeletePreferenceMutation
} from 'apis/up';
import { addToastNotification } from 'redux/actions';

const annotationSuccessStates = {
  CREATE: { description: 'created successfully.' },
  UPDATE: { description: 'updated successfully.' },
  DELETE: { description: 'deleted successfully.' }
};

const annotationErrorStates = {
  CREATE: { description: 'failed to create.' },
  UPDATE: { description: 'failed to update.' },
  DELETE: { description: 'failed to delete.' }
};

const searchAnnotationsParams = { category: USER_PREFERENCE_SETS.ANNOTATION };
const placeholderData = [];

function useUserAnnotations({ devices, from, to, enabled = false }) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [deviceId] = devices;

  const notifySuccess = useCallback(
    (params) => {
      dispatch(
        addToastNotification({
          id: v4(),
          title: 'Chart marker',
          ...params
        })
      );
    },
    [dispatch]
  );
  const notifyError = useCallback(
    (params) => {
      dispatch(
        addToastNotification({
          id: v4(),
          title: 'Chart marker',
          ...params
        })
      );
    },
    [dispatch]
  );
  const onSuccess = useCallback(
    (state) => {
      const queryKey = useSearchPreferencesQuery.getQueryKey(searchAnnotationsParams);

      queryClient.invalidateQueries({ queryKey });

      notifySuccess({
        description: state.description
      });
    },
    [queryClient, notifySuccess]
  );
  const onError = useCallback(
    (state) => {
      notifyError({
        description: state.description
      });
    },
    [notifyError]
  );
  const {
    data: preferences,
    error,
    isLoading,
    isFetching
  } = useSearchPreferencesQuery(searchAnnotationsParams, {
    enabled,
    placeholderData
  });
  const { mutate: createPreference, isLoading: isAdding } = useCreatePreferenceMutation({
    onSuccess: () => onSuccess(annotationSuccessStates.CREATE),
    onError: () => onError(annotationErrorStates.CREATE)
  });
  const { mutate: updatePreference, isLoading: isUpdating } = useUpdatePreferenceMutation({
    onSuccess: () => onSuccess(annotationSuccessStates.UPDATE),
    onError: () => onError(annotationErrorStates.UPDATE)
  });
  const { mutate: deletePreference, isLoading: isRemoving } = useDeletePreferenceMutation({
    onSuccess: () => onSuccess(annotationSuccessStates.DELETE),
    onError: () => onError(annotationErrorStates.DELETE)
  });
  const createAnnotation = useCallback(
    (payload) =>
      createPreference({
        ...searchAnnotationsParams,
        ...fromAnnotation(payload, { devices: [deviceId] })
      }),
    [createPreference, deviceId]
  );
  const updateAnnotation = useCallback(
    ({ annotationId, ...rest }) =>
      updatePreference({
        preferenceId: annotationId,
        ...fromAnnotation(rest, { devices: [deviceId] })
      }),
    [updatePreference, deviceId]
  );
  const deleteAnnotation = useCallback(
    ({ annotationId }) => deletePreference({ preferenceId: annotationId }),
    [deletePreference]
  );
  const data = useMemo(() => {
    const fromTime = from ? new Date(from).getTime() : 0;
    const toTime = to ? new Date(to).getTime() : Date.now();
    const timeRange = [fromTime, toTime];

    return (preferences ?? [])
      .filter((preference) => matchDevices(preference, [deviceId]) && matchTimeRange(preference, timeRange))
      .map(toAnnotation);
  }, [preferences, from, to, deviceId]);

  return {
    data,
    error,
    isLoading,
    isFetching,
    isMutating: isAdding || isUpdating || isRemoving,
    createAnnotation,
    updateAnnotation,
    deleteAnnotation
  };
}

function matchDevices(preference, devices) {
  if (devices.length === 0) {
    return true;
  }

  return (preference.state?.tags?.devices ?? []).some((deviceId) => devices.includes(deviceId));
}

function matchTimeRange(preference, range) {
  const timestamp = new Date(preference.state?.timestamp ?? 0);

  return range[0] <= timestamp && timestamp <= range[1];
}

function fromAnnotation({ name, time }, { devices }) {
  const preference = {};

  if (name != null) {
    preference.name = name;
  }

  if (time != null) {
    preference.state = {
      type: 'line',
      timestamp: new Date(time).toISOString(),
      tags: { devices }
    };
  }

  preference.default = false;

  return preference;
}

function toAnnotation({ preferenceId, name, state }) {
  return {
    annotationId: preferenceId,
    name,
    time: new Date(state.timestamp).getTime()
  };
}

export { useUserAnnotations };
